import { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import {
  Search,
  Building2,
  CreditCard,
  Users,
  TrendingDown,
  Clock,
  Filter,
  Download,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { formatDate, formatCurrency } from "@/lib/demo-data";
import { toast } from "sonner";
import { PLAN_LABELS } from "@/lib/planLabels";

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  TRIAL: { label: "Essai", variant: "outline" },
  ACTIVE: { label: "Actif", variant: "default" },
  PAST_DUE: { label: "Impayé", variant: "destructive" },
  CANCELLED: { label: "Résilié", variant: "destructive" },
  SUSPENDED: { label: "Suspendu", variant: "secondary" },
};

const LIFECYCLE_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  lead: { label: "Lead", variant: "outline" },
  trial: { label: "Essai", variant: "secondary" },
  client: { label: "Client", variant: "default" },
  churned: { label: "Churned", variant: "destructive" },
};

const PLAN_BADGE: Record<string, { label: string; className: string }> = {
  DECOUVERTE: { label: "Découverte", className: "bg-gray-100 text-gray-600 border-gray-200" },
  ARTISAN: { label: "Artisan", className: "bg-blue-50 text-blue-700 border-blue-200" },
  PRO_BTP: { label: "Pro BTP", className: "bg-violet-50 text-violet-700 border-violet-200" },
  ENTERPRISE: { label: "Entreprise", className: "bg-amber-50 text-amber-700 border-amber-200" },
};

type StatusFilter = "all" | "TRIAL" | "ACTIVE" | "PAST_DUE" | "CANCELLED" | "SUSPENDED";
type LifecycleFilter = "all" | "lead" | "trial" | "client" | "churned";
type SortBy = "createdAt_desc" | "createdAt_asc" | "name_asc" | "name_desc" | "mrr_desc" | "expires_asc";

export default function Subscriptions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    (searchParams.get("status") as StatusFilter) || "all"
  );
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleFilter>(
    (searchParams.get("lifecycle") as LifecycleFilter) || "all"
  );
  const [sortBy, setSortBy] = useState<SortBy>("createdAt_desc");

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      const params = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v && v !== "all" && v !== "1") params.set(k, v);
        else params.delete(k);
      });
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const { data, isLoading } = trpc.admin.companies.list.useQuery({
    page,
    limit: 25,
    search: search || undefined,
    product: "saas" as any,
    lifecycle: lifecycleFilter !== "all" ? lifecycleFilter : undefined,
  });

  const items = useMemo(() => {
    if (!data?.items) return [];
    let filtered = data.items;

    if (statusFilter !== "all") {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    if (sortBy === "name_asc") {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "name_desc") {
      filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === "createdAt_asc") {
      filtered = [...filtered].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    } else if (sortBy === "mrr_desc") {
      filtered = [...filtered].sort((a, b) => {
        const mrrA = a.pricePerSeat ? a.pricePerSeat * ((a.includedSeats ?? 1) + (a.additionalSeats ?? 0)) : 0;
        const mrrB = b.pricePerSeat ? b.pricePerSeat * ((b.includedSeats ?? 1) + (b.additionalSeats ?? 0)) : 0;
        return mrrB - mrrA;
      });
    } else if (sortBy === "expires_asc") {
      filtered = [...filtered].sort((a, b) => {
        if (!a.planExpiresAt && !b.planExpiresAt) return 0;
        if (!a.planExpiresAt) return 1;
        if (!b.planExpiresAt) return -1;
        return new Date(a.planExpiresAt).getTime() - new Date(b.planExpiresAt).getTime();
      });
    }

    return filtered;
  }, [data?.items, statusFilter, sortBy]);

  const kpis = useMemo(() => {
    const all = data?.items || [];
    const active = all.filter((c) => c.lifecycle === "client").length;
    const trial = all.filter((c) => c.lifecycle === "trial").length;
    const churned = all.filter((c) => c.lifecycle === "churned").length;
    const now = Date.now();
    const weekMs = 7 * 86400000;
    const trialsExpiringThisWeek = all.filter(
      (c) => c.planExpiresAt && c.lifecycle === "trial" && new Date(c.planExpiresAt).getTime() - now < weekMs && new Date(c.planExpiresAt).getTime() > now
    ).length;
    const mrrTotal = all.reduce((sum, c) => {
      if (c.lifecycle !== "client" || !c.pricePerSeat) return sum;
      return sum + c.pricePerSeat * ((c.includedSeats ?? 1) + (c.additionalSeats ?? 0));
    }, 0);
    const paidCount = all.filter((c) => c.lifecycle === "client").length;
    const trialConversion = trial + paidCount > 0 ? Math.round((paidCount / (trial + paidCount)) * 100) : 0;
    return { total: all.length, active, trial, churned, trialsExpiringThisWeek, mrrTotal, trialConversion };
  }, [data?.items]);

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  const exportCsv = useCallback(() => {
    if (!items.length) return;
    const headers = ["Entreprise", "Email", "SIRET", "Plan", "Statut", "Seats inclus", "Seats add.", "Prix/seat", "MRR", "Expiration", "Inscrit le"];
    const rows = items.map((c) => {
      const totalSeats = (c.includedSeats ?? 1) + (c.additionalSeats ?? 0);
      const mrr = c.pricePerSeat ? c.pricePerSeat * totalSeats : 0;
      return [
        c.name,
        c.email || "",
        c.siret || "",
        PLAN_LABELS[c.plan as keyof typeof PLAN_LABELS] ?? c.plan ?? "Découverte",
        STATUS_BADGE[c.status]?.label || c.status || "",
        String(c.includedSeats ?? 1),
        String(c.additionalSeats ?? 0),
        c.pricePerSeat != null ? String(c.pricePerSeat) : "",
        mrr > 0 ? String(mrr) : "",
        c.planExpiresAt ? formatDate(c.planExpiresAt) : "",
        formatDate(c.createdAt),
      ];
    });
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `abonnements-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Export téléchargé — ${items.length} abonnements`);
  }, [items]);

  if (isLoading) {
    return (
      <AdminLayout>
        <ListPageSkeleton rows={8} />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold">Abonnements SaaS</h1>
            <p className="text-muted-foreground">
              Vue d'ensemble des abonnements et conversions
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!items.length}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Abonnements actifs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{kpis.active}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">MRR estimé</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">{formatCurrency(kpis.mrrTotal)}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Trials en cours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-500" />
                <span className="text-2xl font-bold text-orange-500">{kpis.trial}</span>
              </div>
              {kpis.trialsExpiringThisWeek > 0 && (
                <p className="text-xs text-destructive mt-1">{kpis.trialsExpiringThisWeek} expire(nt) cette semaine</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Churn</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-500" />
                <span className="text-2xl font-bold text-red-500">{kpis.churned}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Conversion trial→paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{kpis.trialConversion}%</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par nom, email, SIRET..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                      updateParams({ q: e.target.value, page: "1" });
                    }}
                    className="pl-10"
                  />
                </div>
              </div>
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v as StatusFilter);
                  setPage(1);
                  updateParams({ status: v, page: "1" });
                }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="TRIAL">Essai</SelectItem>
                  <SelectItem value="ACTIVE">Actif</SelectItem>
                  <SelectItem value="PAST_DUE">Impayé</SelectItem>
                  <SelectItem value="CANCELLED">Résilié</SelectItem>
                  <SelectItem value="SUSPENDED">Suspendu</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={lifecycleFilter}
                onValueChange={(v) => {
                  setLifecycleFilter(v as LifecycleFilter);
                  setPage(1);
                  updateParams({ lifecycle: v, page: "1" });
                }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Lifecycle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous cycles</SelectItem>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="trial">Essai</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="churned">Churned</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={sortBy}
                onValueChange={(v) => setSortBy(v as SortBy)}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Tri" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt_desc">Plus récent</SelectItem>
                  <SelectItem value="createdAt_asc">Plus ancien</SelectItem>
                  <SelectItem value="mrr_desc">MRR décroissant</SelectItem>
                  <SelectItem value="expires_asc">Expiration proche</SelectItem>
                  <SelectItem value="name_asc">Nom A→Z</SelectItem>
                  <SelectItem value="name_desc">Nom Z→A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[240px]">Entreprise</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Seats</TableHead>
                  <TableHead>MRR</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Inscrit le</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                      Aucun abonnement trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((company) => {
                    const statusCfg = STATUS_BADGE[company.status] || {
                      label: company.status || "N/A",
                      variant: "outline" as const,
                    };
                    const planCfg = PLAN_BADGE[company.plan ?? "DECOUVERTE"] || PLAN_BADGE.DECOUVERTE;
                    const totalSeats = (company.includedSeats ?? 1) + (company.additionalSeats ?? 0);
                    const mrr = company.pricePerSeat ? company.pricePerSeat * totalSeats : 0;
                    const expiresAt = company.planExpiresAt ? new Date(company.planExpiresAt) : null;
                    const now = Date.now();
                    const daysUntilExpiry = expiresAt ? Math.ceil((expiresAt.getTime() - now) / 86400000) : null;

                    return (
                      <TableRow
                        key={company.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/companies/${company.id}?tab=billing`)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                              <Building2 className="h-4 w-4 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium truncate">{company.name}</div>
                              {company.email && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {company.email}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={planCfg.className}>
                            {planCfg.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {company.includedSeats ?? 1}
                          {(company.additionalSeats ?? 0) > 0 && (
                            <span className="text-muted-foreground"> +{company.additionalSeats}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {mrr > 0 ? formatCurrency(mrr) : "—"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {expiresAt ? (
                            <span
                              className={
                                daysUntilExpiry !== null && daysUntilExpiry < 0
                                  ? "text-destructive font-medium"
                                  : daysUntilExpiry !== null && daysUntilExpiry < 7
                                    ? "text-orange-600 font-medium"
                                    : daysUntilExpiry !== null && daysUntilExpiry < 30
                                      ? "text-yellow-600"
                                      : "text-muted-foreground"
                              }
                            >
                              {formatDate(company.planExpiresAt!)}
                              {daysUntilExpiry !== null && daysUntilExpiry < 30 && (
                                <span className="block text-xs">
                                  {daysUntilExpiry < 0 ? "Expiré" : `${daysUntilExpiry}j`}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(company.createdAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {company.stripeCustomerId && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Ouvrir dans Stripe"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(
                                    `https://dashboard.stripe.com/customers/${company.stripeCustomerId}`,
                                    "_blank"
                                  );
                                }}
                              >
                                <CreditCard className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/companies/${company.id}?tab=billing`);
                              }}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page} sur {totalPages} — {data?.total ?? 0} résultats
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => {
                  setPage(page - 1);
                  updateParams({ page: String(page - 1) });
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => {
                  setPage(page + 1);
                  updateParams({ page: String(page + 1) });
                }}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
